<script setup>
import {computed, ref, nextTick, onMounted, watch} from 'vue'
import { useProjectStore } from '@/stores/project'
import { useOrgStore } from '@/stores/org'
import { useBoundaryStore } from '@/stores/boundary'
import { useAccessStore } from '@/stores/access'
import {useRoute, useRouter} from 'vue-router'

import {ProjectListItem, ProjectCard} from '@/components/project-list'
import PlaceBrowser from './PlaceBrowser.vue'
import {Spinner} from '@/components/ui/spinner'
import {Input} from '@/components/ui/input'
import {Search, X} from 'lucide-vue-next'
import {toast} from '@/components/ui/toast'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import {useProjectListView} from '@/composables/useProjectListView'
import {useProjectPins} from '@/composables/useProjectPins'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const projectStore = useProjectStore()

const orgStore = useOrgStore()

const boundaryStore = useBoundaryStore()

const accessStore = useAccessStore()
const router = useRouter()
const route = useRoute()

const projects = computed(() => projectStore.projects)
const selectedOrgId = computed(() => orgStore.selectedOrg)
const isLoading = computed(() => projectStore.isLoading)

const orgId = computed(() => parseInt(selectedOrgId.value))
const accessView = computed(() => accessStore.viewFor(orgId.value))
const otherProjects = computed(() => accessView.value ? projectStore.getJoinableProjects : [])
const defaultJoinRole = computed(() => accessStore.defaultJoinRoleName)

const tabs = [
  { value: 'mine', label: 'Joined' },
  { value: 'browse', label: 'Can join' },
]
const tab = ref('mine')
const showTabs = computed(() => otherProjects.value.length > 0)

watch([showTabs, selectedOrgId], () => { tab.value = 'mine' })

const joinDialog = ref(null)
const pendingJoin = ref(null)

const joinProject = (project) => {
  pendingJoin.value = project
  joinDialog.value?.open()
}

const confirmJoin = async () => {
  const project = pendingJoin.value
  if (!project) return
  pendingJoin.value = null
  try {
    await projectStore.joinProject(project.project_id)
    tab.value = 'mine'
    toast({ title: `Joined ${project.project_name}` })
    router.push({ name: 'ProjectHome', params: { org_id: orgId.value, project_id: project.project_id } })
  } catch (e) {
    toast({ title: e.response?.data?.detail ?? `Can't join ${project.project_name}`, variant: 'destructive' })
  }
}

const sortByChooses = [
  { value: 'az', label: 'A-Z' },
  { value: 'za', label: 'Z-A' },
  { value: 'created_at', label: 'Created At' },
  { value: 'last_activity', label: 'Last Activity' },
  { value: 'location', label: 'Location' },
]
const storedSort = localStorage.getItem('projectListSort')
const sortBy = ref(sortByChooses.some(c => c.value === storedSort) ? storedSort : 'location')

const compareProjects = (a, b) => {
  if (sortBy.value === 'az' || sortBy.value === 'location') {
    return a.project_name.localeCompare(b.project_name)
  } else if (sortBy.value === 'za') {
    return b.project_name.localeCompare(a.project_name)
  } else if (sortBy.value === 'created_at') {
    return new Date(a.create_dttm) - new Date(b.create_dttm);
  } else if (sortBy.value === 'last_activity') {
    return new Date(b.last_activity) - new Date(a.last_activity);
  }
  return 0;
}

const { isPinned } = useProjectPins()

// Pinned first, then the chosen sort order within each group.
const compareWithPins = (a, b) => {
  const pinDiff = Number(isPinned(b.project_id)) - Number(isPinned(a.project_id))
  return pinDiff || compareProjects(a, b)
}

const sortedProjects = computed(() => {
  // Copy rather than sort/assign in place: this reads store state, and mutating it
  // here would invalidate this computed and re-trigger it forever.
  const sortProjects = (projectsArray) => {
    return [...projectsArray]
      .sort(compareWithPins)
      .map(project => project.children?.length
        ? { ...project, children: sortProjects(project.children) }
        : project);
  };

  // Filter projects by selected org only
  const orgProjects = projects.value
    .filter(i => i.organization_id === orgId.value);

  return sortProjects(orgProjects);
});

const { view } = useProjectListView()
const viewChooses = [
  { value: 'cards', label: 'Cards' },
  { value: 'tree', label: 'Tree' },
]

const isPlaceRoute = computed(() => route.name === 'OrgProjectPlaces')
const browseByPlace = computed(() => sortBy.value === 'location')
// Location browsing lists joined and joinable projects together; the Join action marks the difference.
const placeProjects = computed(() => [...flatProjects.value.map(entry => entry.project), ...otherProjects.value])
const joinableIds = computed(() => otherProjects.value.map(project => project.project_id))

const leavePlace = () => {
  if (isPlaceRoute.value) router.push({ name: 'OrgProjects', params: { org_id: route.params.org_id } })
}

watch(sortBy, (value) => localStorage.setItem('projectListSort', value))
watch(browseByPlace, (on) => { if (!on) leavePlace() })

// A place URL only makes sense in location browsing, so opening one switches to it.
watch(isPlaceRoute, (onPlace) => {
  if (onPlace) sortBy.value = 'location'
}, { immediate: true })

const flatProjects = computed(() => {
  const out = []
  const walk = (list, parentName) => {
    for (const project of list) {
      out.push({ project, parentName })
      if (project.children?.length) walk(project.children, project.project_name)
    }
  }
  walk(sortedProjects.value, null)
  // The grid is flat, so pinned cards lead regardless of their depth.
  return [
    ...out.filter(entry => isPinned(entry.project.project_id)),
    ...out.filter(entry => !isPinned(entry.project.project_id)),
  ]
})

const boundaries = ref({})

const loadBoundaries = async () => {
  const ids = flatProjects.value.map(entry => entry.project.project_id)
  if (!ids.length) return
  const features = await boundaryStore.fetchBoundariesByIds(ids)
  boundaries.value = Object.fromEntries(features.map(f => [f.properties.project_id, f]))
}

watch([view, selectedOrgId, () => flatProjects.value.length], () => {
  if (view.value === 'cards') loadBoundaries()
}, { immediate: true })

const openedProjectIds = ref(localStorage.getItem('openedProjectIds') ? JSON.parse(localStorage.getItem('openedProjectIds')) : {})

const select = (project, level) => {
  if (!!openedProjectIds.value?.[project.project_id]) {
    delete openedProjectIds.value[project.project_id]
  } else {
    openedProjectIds.value[project.project_id] = {
      level: level,
      project: project
    }
  }

  localStorage.setItem('openedProjectIds', JSON.stringify(openedProjectIds.value))
}

const searchOpen = ref(false)
const searchQuery = ref('')
const searchInput = ref(null)

const openSearch = async () => {
  searchOpen.value = true
  await nextTick()
  searchInput.value?.$el?.focus()
}

const closeSearch = () => {
  searchQuery.value = ''
  searchOpen.value = false
}

const matchesQuery = (name) => {
  const q = searchQuery.value.trim().toLowerCase()
  return !q || (name || '').toLowerCase().includes(q)
}

const filteredFlatProjects = computed(() => {
  if (!searchQuery.value.trim()) return flatProjects.value
  return flatProjects.value.filter(
    (entry) => matchesQuery(entry.project.project_name) || matchesQuery(entry.parentName)
  )
})

// Tree keeps a node when it or any descendant matches.
const filteredTreeProjects = computed(() => {
  if (!searchQuery.value.trim()) return sortedProjects.value
  const filterTree = (list) => list
    .map((project) => {
      const children = project.children?.length ? filterTree(project.children) : []
      if (!matchesQuery(project.project_name) && !children.length) return null
      return { ...project, children }
    })
    .filter(Boolean)
  return filterTree(sortedProjects.value)
})

const sortedOtherProjects = computed(() =>
  [...otherProjects.value]
    .filter((project) => matchesQuery(project.project_name))
    .sort(compareProjects)
)

// While searching, every surviving branch is force-expanded so matches are visible.
const effectiveOpenedIds = computed(() => {
  if (!searchQuery.value.trim()) return openedProjectIds.value
  const opened = {}
  const walk = (list) => {
    for (const project of list) {
      if (project.children?.length) {
        opened[project.project_id] = true
        walk(project.children)
      }
    }
  }
  walk(filteredTreeProjects.value)
  return opened
})

// Fetch projects when component mounts
const fetchBrowseProjects = () => {
  if (accessView.value) projectStore.fetchJoinableProjects()
}

onMounted(async () => {
  if (selectedOrgId.value) {
    await projectStore.fetchProjects()
    fetchBrowseProjects()
  }
})

// Watch for organization changes and fetch projects
watch(selectedOrgId, async (newOrgId) => {
  if (newOrgId) {
    await projectStore.fetchProjects()
    fetchBrowseProjects()
  }
})

watch(() => !!accessView.value, (loaded) => { if (loaded) fetchBrowseProjects() })
</script>

<template>
  <div class="px-2 mt-2 h-8 flex items-center justify-between gap-2">
    <div class="flex items-center shrink-0 gap-3">
      <div v-if="showTabs && !browseByPlace && !searchOpen" class="flex shrink-0 gap-0.5 rounded-lg bg-muted p-0.5">
        <button
          v-for="t in tabs"
          :key="t.value"
          type="button"
          class="rounded-md px-2 py-0.5 text-xs font-medium transition-colors"
          :class="tab === t.value ? 'bg-background shadow font-semibold' : 'text-muted-foreground hover:text-foreground'"
          @click="tab = t.value"
        >
          {{ t.label }}
        </button>
      </div>
      <div class="flex items-center">
        <span class="mr-2 text-xs text-muted-foreground">Sort By:</span>
        <Select v-model="sortBy">
          <SelectTrigger aria-label="Sort by" class="flex-1 max-w-[150px] border-0 p-0 h-auto text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem v-for="item in sortByChooses" :value="item.value" :key="item.value">
                {{ item.label }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div class="flex items-center">
        <span class="mr-2 text-xs text-muted-foreground">View:</span>
        <Select v-model="view">
          <SelectTrigger aria-label="View" class="flex-1 max-w-[150px] border-0 p-0 h-auto text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem v-for="item in viewChooses" :value="item.value" :key="item.value">
                {{ item.label }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>

    <div
      v-if="searchOpen"
      class="relative min-w-0 flex-1 transition-all duration-200"
    >
      <Search class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        ref="searchInput"
        v-model="searchQuery"
        placeholder="Search projects..."
        class="h-8 w-full pl-8 pr-8 text-sm"
        @keydown.escape="closeSearch"
      />
      <button
        type="button"
        class="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        title="Clear search"
        aria-label="Clear search"
        @click="closeSearch"
      >
        <X class="h-4 w-4" />
      </button>
    </div>
    <button
      v-else
      type="button"
      class="h-8 w-8 shrink-0 flex items-center justify-center rounded-md bg-muted text-muted-foreground transition-colors hover:text-foreground"
      title="Search projects"
      aria-label="Search projects"
      @click="openSearch"
    >
      <Search class="h-4 w-4" />
    </button>
  </div>

  <div class="border-b border-border py-1">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <Spinner class="h-6 w-6" />
      <span class="ml-2 text-sm text-muted-foreground">Loading projects...</span>
    </div>

    <PlaceBrowser
      v-else-if="browseByPlace"
      :projects="placeProjects"
      :mode="view"
      :joinable-ids="joinableIds"
      :boundaries="boundaries"
      :search-query="searchQuery"
      @join="joinProject"
    />

    <template v-else-if="tab === 'mine'">
      <!-- Flat Cards -->
      <div v-if="view === 'cards'" class="grid grid-cols-2 gap-2 px-2 py-1">
        <ProjectCard
          v-for="entry in filteredFlatProjects"
          :key="entry.project.project_id"
          :project="entry.project"
          :parent-name="entry.parentName"
          :boundary="boundaries[entry.project.project_id] || null"
        />
      </div>

      <!-- Projects List -->
      <div v-else>
        <ProjectListItem
          v-for="project in filteredTreeProjects"
          :key="project.project_id"
          :project="project"
          :opened-project-ids="effectiveOpenedIds"
          @select="(project, level) => select(project, level + 1)"
        />
      </div>

      <div
        v-if="(view === 'cards' ? filteredFlatProjects : filteredTreeProjects).length === 0"
        class="flex items-center justify-center py-8"
      >
        No projects found
      </div>
    </template>

    <template v-else>
      <p class="px-3 py-1 text-xs text-muted-foreground" data-testid="joinable-hint">
        Projects you can join. You join as {{ defaultJoinRole }}; a project admin can change your role later.
      </p>
      <div v-if="view === 'cards'" class="grid grid-cols-2 gap-2 px-2 py-1">
        <ProjectCard
          v-for="project in sortedOtherProjects"
          :key="project.project_id"
          :project="project"
          :boundary="null"
          joinable
          @join="joinProject"
        />
      </div>

      <div v-else>
        <ProjectListItem
          v-for="project in sortedOtherProjects"
          :key="project.project_id"
          :project="project"
          :opened-project-ids="{}"
          joinable
          @join="joinProject"
        />
      </div>

      <div v-if="sortedOtherProjects.length === 0" class="flex items-center justify-center py-8">
        No projects to join
      </div>
    </template>

    <DialogConfirm
      ref="joinDialog"
      :title="`Join ${pendingJoin?.project_name ?? 'project'}?`"
      :description="`You'll join as ${defaultJoinRole}, the organization's default role for joining. A project admin can change your role later.`"
      confirm-text="Join"
      @confirm="confirmJoin"
      @cancel="pendingJoin = null"
    />
  </div>
</template>
